export type PasswordStrengthLevel = "weak" | "fair" | "medium" | "strong";

export interface PasswordStrengthChecks {
  length: boolean;
  upper: boolean;
  lower: boolean;
  number: boolean;
  special: boolean;
}

export interface PasswordStrength {
  percent: number;
  level: PasswordStrengthLevel;
  checks: PasswordStrengthChecks;
}

/**
 * Computes password strength from length and character variety.
 * Criteria: 8+ chars, uppercase, lowercase, number, special character.
 * Percent = 20% per criterion met; level from count (0-1 weak, 2 fair, 3 medium, 4-5 strong).
 */
export function getPasswordStrength(password: string): PasswordStrength {
  const length = password.length >= 8;
  const upper = /[A-Z]/.test(password);
  const lower = /[a-z]/.test(password);
  const number = /\d/.test(password);
  const special = /[^A-Za-z0-9]/.test(password);

  const checks: PasswordStrengthChecks = {
    length,
    upper,
    lower,
    number,
    special,
  };
  const count = [length, upper, lower, number, special].filter(Boolean).length;
  const percent = count * 20;

  let level: PasswordStrengthLevel = "weak";
  if (count >= 4) level = "strong";
  else if (count === 3) level = "medium";
  else if (count === 2) level = "fair";

  return { percent, level, checks };
}
